import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, MapPin, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

export default function RegisterQR() {
  const { token } = useParams();
  const { loginWith } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showReset, setShowReset] = useState(false);

  useEffect(() => {
    api.get(`/public/course/${token}`)
      .then(({ data }) => setCourse(data))
      .catch(() => setNotFound(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/register-qr/${token}`, { email, password });
      loginWith(data.token, data.user);
      toast.success("Anmeldung erfolgreich. Dieser Kurs wurde Ihrem Konto hinzugefügt.");
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("existiert")) setShowReset(true);
      toast.error(formatApiErrorDetail(detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const forgotPw = async () => {
    if (!email) return toast.error("Bitte zuerst Ihre E-Mail-Adresse eingeben.");
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("Falls ein Konto existiert, wurde eine E-Mail zum Zurücksetzen des Passworts versendet.");
    } catch {
      toast.error("Anfrage fehlgeschlagen.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md">
        <Logo className="mb-8 justify-center" />
        {notFound ? (
          <div className="bg-white border border-border rounded-lg shadow-sm p-8 text-center" data-testid="course-not-found">
            <AlertCircle className="mx-auto text-destructive mb-3" size={32} />
            <h1 className="text-xl font-serif mb-2">Kurs nicht gefunden</h1>
            <p className="text-sm text-muted-foreground">Der Link ist ungültig oder wurde entfernt.</p>
          </div>
        ) : !course ? (
          <p className="text-center text-muted-foreground">Wird geladen…</p>
        ) : course.expired ? (
          <div className="bg-white border border-border rounded-lg shadow-sm p-8 text-center" data-testid="course-offline">
            <AlertCircle className="mx-auto text-warning mb-3" size={32} style={{ color: "#D97706" }} />
            <h1 className="text-xl font-serif mb-2">{course.kurs_name}</h1>
            <p className="text-sm text-muted-foreground">
              Dieser Kurs ist derzeit offline. Eine Anmeldung ist nicht mehr möglich.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-lg shadow-sm p-8">
            <h1 className="text-2xl font-serif mb-1" data-testid="course-title">{course.kurs_name}</h1>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mb-5">
              <span className="flex items-center gap-1"><MapPin size={14} />{course.kurs_gemeinde}</span>
              {course.kurs_beginn && <span className="flex items-center gap-1"><CalendarDays size={14} />{course.kurs_beginn}</span>}
            </div>
            {course.beschreibung && <p className="text-sm text-slate-600 mb-6 whitespace-pre-line">{course.beschreibung}</p>}
            <p className="text-sm text-muted-foreground mb-4">
              Bitte legen Sie ein Konto an, um Ihr Kind anzumelden. Mit einer E-Mail-Adresse können Sie mehrere Kinder zu verschiedenen Kursen anmelden. Falls Sie bereits ein Konto haben, geben Sie einfach Ihr vorhandenes Passwort ein – dieser Kurs wird dann Ihrem Konto hinzugefügt.
            </p>
            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail-Adresse (Eltern)</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="register-password" />
              </div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit-button">
                {busy ? "Wird angemeldet…" : "Konto anlegen / anmelden & Kurs hinzufügen"}
              </Button>
            </form>
            {showReset && (
              <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="qr-existing-hint">
                Diese E-Mail ist bereits registriert. Bitte melden Sie sich mit Ihrem vorhandenen Passwort an – dann wird dieser Kurs hinzugefügt. Passwort vergessen? Nutzen Sie den Link unten.
              </div>
            )}
            <div className="mt-3 flex items-center justify-between text-xs">
              <Link to="/login" className="text-primary hover:underline">Zur Anmeldung</Link>
              <button type="button" onClick={forgotPw} className="text-primary hover:underline" data-testid="qr-forgot-password">Passwort zurücksetzen</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
